import React from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';

const THEME_COLOR = '#009688';
const COMPLETE_COLOR = '#4CAF50';

const StepContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '16px 24px',
  backgroundColor: '#fafafa',
  borderBottom: '1px solid #e0e0e0',
  gap: 0,
});

const StepItem = styled(Box)(({ status }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
}));

const StepCircle = styled(Box)(({ status }) => ({
  width: 28,
  height: 28,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 12,
  fontWeight: 600,
  transition: 'all 0.3s ease',
  ...(status === 'complete' && {
    backgroundColor: COMPLETE_COLOR,
    color: '#fff',
  }),
  ...(status === 'current' && {
    backgroundColor: THEME_COLOR,
    color: '#fff',
    boxShadow: `0 0 0 4px ${THEME_COLOR}25`,
  }),
  ...(status === 'pending' && {
    backgroundColor: '#fff',
    color: '#9e9e9e',
    border: '2px solid #e0e0e0',
  }),
}));

const StepLabel = styled(Typography)(({ status }) => ({
  fontSize: 13,
  fontWeight: status === 'current' ? 600 : 500,
  color: status === 'pending' ? '#9e9e9e' : status === 'current' ? THEME_COLOR : '#424242',
  transition: 'all 0.3s ease',
}));

const Connector = styled(Box)(({ completed }) => ({
  height: 2,
  width: 60,
  margin: '0 12px',
  backgroundColor: completed ? COMPLETE_COLOR : '#e0e0e0',
  transition: 'background-color 0.3s ease',
}));

const StepProgress = ({
  serviceSelected = false,
  employeeSelected = false,
  detailsComplete = false,
  formValid = false,
}) => {
  const getStepStatus = (stepIndex) => {
    switch (stepIndex) {
      case 0: // Service
        return serviceSelected ? 'complete' : 'current';
      case 1: // Employee
        if (!serviceSelected) return 'pending';
        return employeeSelected ? 'complete' : 'current';
      case 2: // Details
        if (!serviceSelected || !employeeSelected) return 'pending';
        return detailsComplete ? 'complete' : 'current';
      case 3: // Submit
        if (!serviceSelected || !employeeSelected) return 'pending';
        return formValid ? 'current' : 'pending';
      default:
        return 'pending';
    }
  };

  const steps = [
    { label: 'Service', number: 1 },
    { label: 'Employee', number: 2 },
    { label: 'Details', number: 3 },
    { label: 'Submit', number: 4 },
  ];

  return (
    <StepContainer>
      {steps.map((step, index) => {
        const status = getStepStatus(index);
        const isComplete = status === 'complete';
        const showConnector = index < steps.length - 1;
        const nextStatus = index < steps.length - 1 ? getStepStatus(index + 1) : 'pending';
        const isConnectorComplete = isComplete && nextStatus !== 'pending';

        return (
          <React.Fragment key={step.label}>
            <StepItem status={status}>
              <StepCircle status={status}>
                {isComplete ? (
                  <CheckIcon sx={{ fontSize: 16 }} />
                ) : (
                  step.number
                )}
              </StepCircle>
              <StepLabel status={status}>
                {step.label}
              </StepLabel>
            </StepItem>
            {showConnector && (
              <Connector completed={isConnectorComplete} />
            )}
          </React.Fragment>
        );
      })}
    </StepContainer>
  );
};

export default StepProgress;
